/**
 * Session Error Utility
 * Provides user-friendly, actionable error messages for counselors
 */

/**
 * Parse session-related errors and return user-friendly messages
 * @param {Error} error - The caught error object
 * @param {string} context - The context in which the error occurred (e.g., 'consent', 'intake')
 * @returns {string} User-friendly error message
 */
export const parseSessionError = (error, context = 'session') => {
    // Get the raw error message
    const rawMessage = error?.response?.data?.message
        || error?.message
        || 'An unexpected error occurred';

    // Get HTTP status code if available
    const statusCode = error?.response?.status;

    // Context-specific step names for better messaging
    const stepNames = {
        consent: 'Consent Form',
        intake: 'Intake Assessment',
        mentalStatus: 'Mental Status Exam',
        safety: 'Safety Assessment',
        record: 'Session Notes',
        termination: 'Session Termination'
    };

    const stepName = stepNames[context] || context;

    // Handle specific error patterns
    if (rawMessage.includes('validation failed')) {
        // Parse mongoose validation errors
        if (rawMessage.includes('interventionsUsed')) {
            return `${stepName}: There was an issue with the interventions field. Please try again or contact support if the problem persists.`;
        }
        if (rawMessage.includes('required')) {
            return `${stepName}: Some required fields are missing. Please fill in all required fields and try again.`;
        }
        return `${stepName}: Some data is invalid. Please check all fields and try again.`;
    }

    if (rawMessage.includes('Session not found') || statusCode === 404) {
        return 'This session could not be found. It may have been deleted or you may not have access. Please return to your sessions list.';
    }

    if (rawMessage.includes('Not authorized') || statusCode === 403) {
        return 'You do not have permission to modify this session. Only the assigned counselor or HOD can make changes.';
    }

    if (statusCode === 401) {
        return 'Your session has expired. Please log in again to continue.';
    }

    if (statusCode === 500) {
        return `Server error while saving ${stepName}. Please try again in a moment. If this persists, contact IT support.`;
    }

    if (rawMessage.includes('network') || rawMessage.includes('Network')) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    if (rawMessage.includes('timeout') || rawMessage.includes('Timeout')) {
        return 'The request timed out. Please try again.';
    }

    // Default with context
    return `Failed to save ${stepName}. ${rawMessage}. Please try again or contact support if the issue continues.`;
};

/**
 * Get error recovery suggestions based on error type
 * @param {Error} error - The caught error object
 * @param {string} context - The context in which the error occurred
 * @returns {string[]} Array of recovery suggestions
 */
export const getErrorRecoverySuggestions = (error, context = 'session') => {
    const suggestions = [];
    const rawMessage = error?.response?.data?.message || error?.message || '';
    const statusCode = error?.response?.status;

    if (statusCode === 401) {
        suggestions.push('Click here to log in again');
        return suggestions;
    }

    if (statusCode === 403) {
        suggestions.push('Contact the assigned counselor or HOD');
        suggestions.push('Return to your sessions list');
        return suggestions;
    }

    if (statusCode === 404) {
        suggestions.push('Return to your sessions list');
        suggestions.push('Start a new session if needed');
        return suggestions;
    }

    if (rawMessage.includes('validation') || rawMessage.includes('required')) {
        suggestions.push('Review all form fields for missing or invalid data');
        suggestions.push('Ensure all required fields are filled in');
    }

    if (statusCode === 500 || rawMessage.includes('server')) {
        suggestions.push('Wait a moment and try again');
        suggestions.push('Your data may still be saved - check after refreshing');
        suggestions.push('Contact IT support if the problem persists');
    }

    if (rawMessage.includes('network') || rawMessage.includes('timeout')) {
        suggestions.push('Check your internet connection');
        suggestions.push('Try refreshing the page');
    }

    // Default suggestions
    if (suggestions.length === 0) {
        suggestions.push('Try saving again');
        suggestions.push('Refresh the page if the problem persists');
        suggestions.push('Contact support if you continue to experience issues');
    }

    return suggestions;
};

export default { parseSessionError, getErrorRecoverySuggestions };
